import { Fragment, type ReactNode } from "react";
import type { Identifier } from "ra-core";
import {
  ListContextProvider,
  ResourceContextProvider,
  useGetMany,
  useListController,
  useTranslate,
} from "ra-core";

import { Separator } from "@/components/ui/separator";
import { Note } from "@/components/atomic-crm/notes/Note";
import { RelativeDate } from "@/components/atomic-crm/misc/RelativeDate";
import type { ContactNote } from "@/components/atomic-crm/types";

export interface TimelineEvent {
  id: string;
  subject_type: "contact" | "company";
  subject_id: number;
  kind: "note" | "stage" | "step" | "email" | "order" | "order_request";
  source_table: string;
  source_id: number;
  deal_id: number | null;
  at: string;
  title: string | null;
  body: string | null;
  channel: string | null;
  outcome: string | null;
  sales_id: number | null;
}

// Timeline of a contact or a company from tenax_timeline (task 1.9, D-038).
// The notes of the subject itself keep Atomic's note, editable as before;
// the other events are short read-only rows. children (the note form) sit
// in the same list context, so a new note reads the timeline again.
export const TenaxTimeline = ({
  subjectType,
  subjectId,
  children,
}: {
  subjectType: "contact" | "company";
  subjectId: Identifier;
  children?: ReactNode;
}) => {
  const list = useListController<TimelineEvent>({
    resource: "tenax_timeline",
    filter: { subject_type: subjectType, subject_id: subjectId },
    sort: { field: "at", order: "DESC" },
    perPage: 100,
    disableSyncWithLocation: true,
    storeKey: false,
  });
  return (
    <ListContextProvider value={list}>
      {children}
      <TimelineRows events={list.data ?? []} subjectType={subjectType} />
    </ListContextProvider>
  );
};

const ownNotesTable = (subjectType: "contact" | "company") =>
  subjectType === "contact" ? "contact_notes" : "company_notes";

const TimelineRows = ({
  events,
  subjectType,
}: {
  events: TimelineEvent[];
  subjectType: "contact" | "company";
}) => {
  const translate = useTranslate();
  const table = ownNotesTable(subjectType);
  const ownIds = events
    .filter((event) => event.source_table === table)
    .map((event) => event.source_id);
  const { data: ownNotes = [] } = useGetMany<ContactNote>(
    table,
    { ids: ownIds },
    { enabled: ownIds.length > 0 },
  );

  if (!events.length) {
    return (
      <p className="mt-4 text-sm text-muted-foreground">
        {translate("tenax.timeline.empty")}
      </p>
    );
  }
  return (
    <ol
      aria-label={translate("tenax.timeline.title")}
      className="mt-4 space-y-3"
    >
      {events.map((event, index) => {
        const note =
          event.source_table === table
            ? ownNotes.find((item) => item.id === event.source_id)
            : undefined;
        if (event.source_table === table && !note) return null;
        return (
          <Fragment key={event.id}>
            <li>
              <ChannelOutcome event={event} />
              {note ? (
                <ResourceContextProvider value={table}>
                  <Note
                    note={note}
                    isCompact={index > 0}
                    showStatus={subjectType === "contact"}
                  />
                </ResourceContextProvider>
              ) : (
                <EventRow event={event} />
              )}
            </li>
            {index < events.length - 1 && <Separator />}
          </Fragment>
        );
      })}
    </ol>
  );
};

const ChannelOutcome = ({ event }: { event: TimelineEvent }) => {
  const translate = useTranslate();
  if (event.kind !== "note" || (!event.channel && !event.outcome)) return null;
  return (
    <p className="text-sm font-medium">
      {[
        event.channel && translate(`tenax.people.channel.${event.channel}`),
        event.outcome && translate(`tenax.timeline.outcome.${event.outcome}`),
      ]
        .filter(Boolean)
        .join(" · ")}
    </p>
  );
};

const EventRow = ({ event }: { event: TimelineEvent }) => {
  const translate = useTranslate();
  const text = (() => {
    switch (event.kind) {
      case "stage":
        return translate("tenax.timeline.stage_to", {
          deal: event.title ?? "",
          stage: event.body ?? "",
        });
      case "step":
        return translate("tenax.timeline.step_done", {
          step: event.body ?? "",
          task: event.title ?? "",
        });
      case "order":
        return [event.title, event.body].filter(Boolean).join(" · ");
      default:
        return [event.title, event.body].filter(Boolean).join(": ");
    }
  })();
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="font-medium">
          {translate(`tenax.timeline.kind.${event.kind}`)}
        </span>
        <span className="flex-1" />
        <RelativeDate date={event.at} />
      </div>
      {text ? <p className="whitespace-pre-line">{text}</p> : null}
    </div>
  );
};
